import axios from "axios";
import { getBaseUrl } from "../helpers/UrlHelper";
import Calculate from "../types/Calculate";
import Color from "../types/Color";
import MountingType from "../types/MountingType";
import MountingTypeField from "../types/MountingTypeField";
import ProductCategory from "../types/ProductCategory";
import ResultValue from "../types/ResultValue";
import Row from "../types/Row";
import ShortSnowbarrierResult from "../types/Snowbarrier-short-result";
import SnowzoneType from "../types/SnowzoneType";

// Function to map countryCode to language code
const mapCountryCodeToLang = (countryCode: string) => {
  switch (countryCode.toLowerCase()) {
    case 'se': // Sweden
      return 'sv';
    case 'fi': // Finland
      return 'fi';
    default:
      return 'en'; // Default to English
  }
};

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    "Content-type": "application/json",
  },
});

apiClient.interceptors.request.use(config => {
  if (!config.url) {
    return config; // If the URL is not defined, return the config as is
  }
  
  const url = new URL(config.url, config.baseURL);
  // Extract countryCode from the search parameters
  const countryCode = url.searchParams.get('countryCode');

  if (countryCode) {
    // Map countryCode to language
    const lang = mapCountryCodeToLang(countryCode);

    // Append the 'lang' query parameter
    url.searchParams.append('lang', lang);
    config.url = url.toString();
  }
  return config;
});

const getProductCategories = async (countryCode: string) => {
  const response = await apiClient.get<ProductCategory[]>(getBaseUrl() +`/ect/roof-safety/product-categories?countryCode=${countryCode.toUpperCase()}`);
  return response.data;
}

const getMountingTypes = async (countryCode: string, productCategoryId: number) => {
  const response = await apiClient.get<MountingType[]>(getBaseUrl() +`/ect/roof-safety/mounting-types?countryCode=${countryCode.toUpperCase()}&productCategoryId=${productCategoryId}`);
  return response.data;
}

const getMountingTypeFields = async (countryCode: string, mountingTypeId: number, productCategoryId: number) => {
  const response = await apiClient.get<MountingTypeField[]>(getBaseUrl() +`/ect/roof-safety/mounting-type-fields?countryCode=${countryCode.toUpperCase()}&mountingTypeId=${mountingTypeId}&productCategoryId=${productCategoryId}`);
  return response.data;
}

const getColors = async (countryCode: string, productCategoryId: number) => {
  const response = await apiClient.get<Color[]>(getBaseUrl() +`/ect/roof-safety/colors?countryCode=${countryCode.toUpperCase()}&productCategoryId=${productCategoryId}`);
  return response.data;
}

const calculate = async (calculate: Calculate, countryCode: string) => {
  const response = await apiClient.post<ResultValue[]>(getBaseUrl() + `/ect/roof-safety/calculate?countryCode=${countryCode.toUpperCase()}`, JSON.stringify(calculate));
  return response.data;
}

const getInformationSign = async (countryCode: string) => {
  const response = await apiClient.get<ResultValue[]>(getBaseUrl() +`/ect/roof-safety/information-sign?countryCode=${countryCode.toUpperCase()}`);
  return response.data;
}

const calculateMaxDistance = async (elevation: Number, zone: number) => {
  const response = await apiClient.get<SnowzoneType[]>(getBaseUrl() +`/ect/roof-safety/snow-zone/max-distance?elevation=${elevation}&zone=${zone}`);
  return response.data;
}
const calculateConsoleDistance = async (elevation: Number, length: number, zone: number) => {
  const response = await apiClient.get<SnowzoneType[]>(getBaseUrl() +`/ect/roof-safety/snow-zone/console-distance?elevation=${elevation}&length=${length}&zone=${zone}`);
  return response.data;
}
const calculateShortSnowbarrier = async (rooftype: string, snowload: number, roofelevation: number, rooflength: number, distanceouterconsole: number, distanceccbrackets: number, distancefromconsoletoroofeave: number, overhang: number) => {
  const response = await apiClient.get<ShortSnowbarrierResult[]>(getBaseUrl() +`/ect/roof-safety/snow-barrier/calculation-of-short-snowbarrier?rooftype=${rooftype}&snowload=${snowload}&roofelevation=${roofelevation}&rooflength=${rooflength}&distanceouterconsole=${distanceouterconsole}&distanceccbrackets=${distanceccbrackets}&distancefromconsoletoroofeave=${distancefromconsoletoroofeave}&overhang=${overhang}`);
  return response.data;
};

const optimizeArticlePackages = async (articleRows: Row[], countryCode: string) => {
  const response = await apiClient.post<{ rows: Row[] }>(getBaseUrl() +`/ect/roof-safety/optimizearticlepackages/`, { "CountryCode":countryCode.toUpperCase(), "Rows": articleRows });
  return response.data;
}


const ApiDataService = {
  getProductCategories,
  getMountingTypes,
  getMountingTypeFields,
  getColors,
  getInformationSign,
  calculate,
  calculateMaxDistance,
  calculateShortSnowbarrier,
  calculateConsoleDistance,
  optimizeArticlePackages
}

export default ApiDataService;
